import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import pt from './pt';
import en from './en';
import type { ApiErrorShape } from '@/lib/api';

export type Lang = 'pt' | 'en';
export type Dict = { [key: string]: string | Dict };
export type Vars = Record<string, string | number | null | undefined>;

const DICTS: Record<Lang, Dict> = { pt, en };
export const LANGS: Lang[] = ['pt', 'en'];

function lookup(dict: Dict, key: string): string | undefined {
    let cur: string | Dict | undefined = dict;
    for (const part of key.split('.')) {
        if (!cur || typeof cur === 'string') return undefined;
        cur = cur[part];
    }
    return typeof cur === 'string' ? cur : undefined;
}

export function translate(lang: Lang, key: string, vars?: Vars): string {
    const s = lookup(DICTS[lang], key) ?? lookup(DICTS.pt, key) ?? key;
    if (!vars) return s;
    return s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] === undefined || vars[k] === null ? '' : String(vars[k])));
}

export function guessLang(code?: string | null): Lang {
    return /^pt/i.test(String(code || '')) ? 'pt' : 'en';
}

export type T = ((key: string, vars?: Vars) => string) & { lang: Lang };

interface I18nValue { lang: Lang; setLang: (l: Lang) => void; t: T; te: (err: ApiErrorShape, extra?: Vars) => string }

const Ctx = createContext<I18nValue | null>(null);

export function I18nProvider({ initial, children }: { initial: Lang; children: ReactNode }) {
    const [lang, setLangState] = useState<Lang>(initial);
    const setLang = useCallback((l: Lang) => {
        setLangState(l);
        document.documentElement.lang = l === 'pt' ? 'pt-BR' : 'en';
    }, []);
    const value = useMemo<I18nValue>(() => {
        const t = ((key: string, vars?: Vars) => translate(lang, key, vars)) as T;
        t.lang = lang;
        /** An API error code in the player's language (errors.<code>, else errors.generic). */
        const te = (err: ApiErrorShape, extra?: Vars) => {
            const vars: Vars = {};
            for (const [k, v] of Object.entries(err || {})) if (typeof v === 'string' || typeof v === 'number') vars[k] = v;
            for (const [k, v] of Object.entries(extra || {})) if (v !== '' && v !== undefined && v !== null) vars[k] = v;   // formatted values win
            // A variant first (errors.create_failed_config…), then the code, then the generic text.
            const variant = typeof (err && err.reason) === 'string' ? `errors.${err.error}_${err.reason}` : null;
            if (variant && (lookup(DICTS[lang], variant) || lookup(DICTS.pt, variant))) return t(variant, vars);
            const key = `errors.${err && err.error}`;
            return lookup(DICTS[lang], key) || lookup(DICTS.pt, key) ? t(key, vars) : t('errors.generic');
        };
        return { lang, setLang, t, te };
    }, [lang, setLang]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nValue {
    const v = useContext(Ctx);
    if (!v) throw new Error('useI18n outside I18nProvider');
    return v;
}
